/* eslint-disable max-lines */
import { getNudgeData } from '@utils/nudgeHelper';
import { goalLevelOnboardingStatus } from '@constants/index';
import {
  authTypes,
  educatorTypes,
  combatTypes,
  topologyTypes
} from '../constants';

const initialState = {
  accessToken: null,
  refreshToken: null,
  me: {},
  credentials: null,
  bioRecommendation: [],
  loggingIn: false,
  loginResponse: null,
  followedGoals: [],
  dailyTask: {},
  selectedNav: 'plus',
  selectedEduNav: 'dashboard',
  isFollowGoalTooltipShowing: false,
  isSwitchGoalTooltipShowing: false,
  countries: [],
  states: [],
  selectedGoal: null,
  otpOrEmailData: null,
  accessControl: {},
  verifyOtpResponse: {
    type: 'phone',
    submitting: false,
    success: false,
    error: false,
    errorMsg: ''
  },
  showLoginModal: false,
  loginType: 'login',
  isLoggedIn: false,
  sendEmailOtpStatus: {
    isLoading: false,
    errorMsg: ''
  },
  verifyEmailOtpStatus: {
    isLoading: false,
    errorMsg: ''
  },
  sendMobileOtpStatus: {
    isLoading: false,
    errorMsg: ''
  },
  verifyMobileOtpStatus: {
    isLoading: false,
    errorMsg: ''
  },
  nudgeData: null,
  isDarkModeOn: false,
  isSubmittingFeedback: false,
  submitFeedbackFailed: false,
  latestStreak: {},
  renewalData: {},
  manageUpdates: [],
  checkPartPaymentStatus: {},
  showReferralBanner: true
};

const updateCouponData = ({ result }) => ({ couponData: result });

const reducerMap = {
  [`${authTypes.GENERATE_CUSTOM_CODE}_SUCCESS`]: updateCouponData,
  [`${authTypes.GET_CUSTOM_CODE_DATA}_SUCCESS`]: updateCouponData,
  [`${authTypes.LOGIN}_REQUEST`]: () => ({ loggingIn: true }),
  [`${authTypes.LOGIN}_SUCCESS`]: ({ result }) => ({
    loginResponse: result,
    loggingIn: false
  }),
  [`${authTypes.LOGIN}_FAILURE`]: ({ error }) => ({
    loginResponse: error,
    loggingIn: false
  })
};

export default function authState(state = initialState, action) {
  switch (action.type) {
    case authTypes.AUTHENTICATE:
      return {
        ...state,
        ...action.payload
      };

    case authTypes.DEAUTHENTICATE:
      return {
        ...initialState
      };

    case `UPDATE_${authTypes.ME}`: {
      const me = action.data && Object.values(action.data)[0];
      const nudgeData = getNudgeData(me);
      return {
        ...state,
        me: {
          ...state.me,
          ...me
        },
        isLoggedIn: true,
        nudgeData
      };
    }

    case 'LOGOUT_SUCCESS': {
      return initialState;
    }

    case `${authTypes.FOLLOWED_GOALS}_SUCCESS`:
      return {
        ...state,
        followedGoals: action.result
      };

    case `${authTypes.DAILY_TASK}_SUCCESS`:
      return {
        ...state,
        dailyTask: action.result || {}
      };

    case `${authTypes.FETCH_COUNTRIES}_SUCCESS`:
      return {
        ...state,
        countries: action.result || {}
      };

    case `${authTypes.FETCH_STATES}_SUCCESS`:
      return {
        ...state,
        states: action.result || {}
      };

    case `${authTypes.FETCH_CREDENTIALS}_SUCCESS`:
      return {
        ...state,
        credentials: action.result || {}
      };

    case `${authTypes.FETCH_BIO_RECOMMENDATION}_SUCCESS`:
      return {
        ...state,
        bioRecommendation: action.result || {}
      };

    case authTypes.SELECTED_GOAL: {
      const { selectedGoal } = action;
      const status =
        selectedGoal?.goalLevelOnboarding?.status ||
        selectedGoal?.user_onboarding_status;
      return {
        ...state,
        selectedGoal,
        accessControl: {
          ...state.accessControl,
          user_onboarding_status: status
        }
      };
    }

    case `${topologyTypes.SAVE_PREFERENCE_FETCH_NEXT}_SUCCESS`: {
      const { isEditing } = action;
      return {
        ...state,
        accessControl: {
          ...state.accessControl,
          user_onboarding_status: isEditing
            ? state.accessControl?.user_onboarding_status
            : goalLevelOnboardingStatus.INCOMPLETE
        }
      };
    }

    case authTypes.SELECTED_NAV:
      return {
        ...state,
        selectedNav: action.selectedNav
      };

    case educatorTypes.SELECTED_NAV_EDU:
      return {
        ...state,
        selectedEduNav: action.selectedNav
      };

    case authTypes.SHOW_FOLLOW_GOAL_TOOLTIP:
      return {
        ...state,
        isFollowGoalTooltipShowing: action.show
      };

    case authTypes.SHOW_SWITCH_GOAL_TOOLTIP:
      return {
        ...state,
        isSwitchGoalTooltipShowing: action.show
      };

    case authTypes.FOLLOW_GOAL:
      return { ...state };

    case authTypes.LOGIN_MODAL:
      return {
        ...state,
        showLoginModal: action.show,
        loginType: action.loginType
      };

    case `${authTypes.RESEND_OTP}_SUCCESS`:
      return {
        ...state,
        otpOrEmailData: action.result
      };

    case authTypes.RESET_OTP_RESPONSE:
      return {
        ...state,
        otpOrEmailData: null
      };

    case authTypes.RESET_VERIFY_OTP_RESPONSE:
      return {
        ...state,
        verifyOtpResponse: {
          type: 'phone',
          submitting: false,
          success: false,
          error: false,
          errorMsg: ''
        }
      };

    case authTypes.VERIFY_PHONE:
      return {
        ...state,
        verifyOtpResponse: {
          type: 'phone',
          submitting: true,
          success: false,
          error: false,
          errorMsg: ''
        }
      };

    case `${authTypes.VERIFY_PHONE}_SUCCESS`:
      return {
        ...state,
        verifyOtpResponse: {
          type: 'phone',
          submitting: false,
          success: true,
          error: false,
          errorMsg: ''
        }
      };

    case `${authTypes.VERIFY_PHONE}_FAILURE`:
      return {
        ...state,
        verifyOtpResponse: {
          type: 'phone',
          submitting: false,
          success: false,
          error: true,
          errorMsg: action.error?.message
        }
      };

    case authTypes.VERIFY_OTP:
      return {
        ...state,
        verifyOtpResponse: {
          type: 'otp',
          submitting: true,
          success: false,
          error: false,
          errorMsg: ''
        }
      };

    case `${authTypes.VERIFY_OTP}_SUCCESS`:
      return {
        ...state,
        verifyOtpResponse: {
          type: 'otp',
          submitting: false,
          success: true,
          error: false,
          errorMsg: ''
        }
      };

    case `${authTypes.VERIFY_OTP}_FAILURE`:
      return {
        ...state,
        verifyOtpResponse: {
          type: 'otp',
          submitting: false,
          success: false,
          error: true,
          errorMsg: action.error?.message
        }
      };

    case authTypes.RESET_NETWORK_CALLS_ERROR:
      return {
        ...state,
        sendEmailOtpStatus: initialState.sendEmailOtpStatus,
        verifyEmailOtpStatus: initialState.verifyEmailOtpStatus,
        sendMobileOtpStatus: initialState.sendMobileOtpStatus,
        verifyMobileOtpStatus: initialState.verifyMobileOtpStatus
      };

    case `${authTypes.SEND_MAIL_OTP}_REQUEST`:
      return {
        ...state,
        sendEmailOtpStatus: {
          isLoading: true,
          errorMsg: ''
        }
      };

    case `${authTypes.SEND_MAIL_OTP}_SUCCESS`:
      return {
        ...state,
        sendEmailOtpStatus: {
          isLoading: false,
          errorMsg: ''
        }
      };

    case `${authTypes.SEND_MAIL_OTP}_FAILURE`:
      return {
        ...state,
        sendEmailOtpStatus: {
          isLoading: false,
          errorMsg: action.error?.message
        }
      };

    case `${authTypes.VERIFY_MAIL_OTP}_REQUEST`:
      return {
        ...state,
        verifyEmailOtpStatus: {
          isLoading: true,
          errorMsg: ''
        }
      };

    case `${authTypes.VERIFY_MAIL_OTP}_SUCCESS`:
      return {
        ...state,
        verifyEmailOtpStatus: {
          isLoading: false,
          errorMsg: ''
        }
      };

    case `${authTypes.VERIFY_MAIL_OTP}_FAILURE`:
      return {
        ...state,
        verifyEmailOtpStatus: {
          isLoading: false,
          errorMsg: action.error?.message
        }
      };

    case `${authTypes.SEND_MOBILE_OTP}_REQUEST`:
      return {
        ...state,
        sendMobileOtpStatus: {
          isLoading: true,
          errorMsg: ''
        }
      };

    case `${authTypes.SEND_MOBILE_OTP}_SUCCESS`:
      return {
        ...state,
        sendMobileOtpStatus: {
          isLoading: false,
          errorMsg: ''
        }
      };

    case `${authTypes.SEND_MOBILE_OTP}_FAILURE`:
      return {
        ...state,
        sendMobileOtpStatus: {
          isLoading: false,
          errorMsg: action.error?.message
        }
      };

    case `${authTypes.VERIFY_MOBILE_OTP}_REQUEST`:
      return {
        ...state,
        verifyMobileOtpStatus: {
          isLoading: true,
          errorMsg: ''
        }
      };

    case `${authTypes.VERIFY_MOBILE_OTP}_SUCCESS`:
      return {
        ...state,
        verifyMobileOtpStatus: {
          isLoading: false,
          errorMsg: ''
        }
      };

    case `${authTypes.VERIFY_MOBILE_OTP}_FAILURE`:
      return {
        ...state,
        verifyMobileOtpStatus: {
          isLoading: false,
          errorMsg: action.error?.message
        }
      };

    case authTypes.HIDE_NUDGE:
      return {
        ...state,
        nudgeData: null
      };

    case `${authTypes.SUBMIT_FEEDBACK}_REQUEST`:
      return {
        ...state,
        isSubmittingFeedback: true,
        submitFeedbackFailed: false
      };

    case `${authTypes.SUBMIT_FEEDBACK}_SUCCESS`:
      return {
        ...state,
        isSubmittingFeedback: false,
        submitFeedbackFailed: false,
        me: {
          ...state.me,
          plusSubscriptions: [
            ...state.me.plusSubscriptions
              .slice()
              .filter(
                (subscription) =>
                  subscription.subscription.value.uid !== action.goalUID
              ),
            {
              ...state.me.plusSubscriptions.find(
                ({ subscription }) => subscription?.value.uid === action.goalUID
              ),
              take_feedback: false
            }
          ]
        }
      };

    case authTypes.SET_DARK_MODE:
      return {
        ...state,
        isDarkModeOn: action.isDarkModeOn
      };

    case `${authTypes.SUBMIT_FEEDBACK}_FAILURE`:
      return {
        ...state,
        isSubmittingFeedback: false,
        submitFeedbackFailed: true
      };

    case `${authTypes.LATEST_STREAK}_SUCCESS`:
      return {
        ...state,
        latestStreak: action.result || {}
      };

    case `${authTypes.UPDATE_AVATAR}_SUCCESS`:
      return {
        ...state,
        me: {
          ...state.me,
          thumbnail: action.result.avatar
        }
      };

    case `${authTypes.FETCH_RENEWAL_TOUCHPOINT_DATA}_SUCCESS`:
      return {
        ...state,
        renewalData: {
          ...state.renewalData,
          [action.goalUID]: action.result
        }
      };

    case `${authTypes.FETCH_SETTINGS_MANAGE_UPDATES}_SUCCESS`:
      return {
        ...state,
        manageUpdates: action.result
      };

    case `${authTypes.CHANGE_MANAGE_UPDATE_PREFERENCES}_REQUEST`: {
      const updatesReq = { ...state.manageUpdates };
      updatesReq[action.blockType].preference[action.key] = action.flag;
      return {
        ...state,
        manageUpdates: updatesReq
      };
    }

    case `${authTypes.FETCH_REFERRAL_DETAILS}_SUCCESS`:
      return {
        ...state,
        me: {
          ...state.me,
          referralInfo: {
            ...state.me.referralInfo,
            referralPoints: parseFloat(action.result.Points, 10) || 0,
            referralCount: parseInt(action.result.referral_counts, 10) || 0
          }
        }
      };

    case `${authTypes.CHANGE_MANAGE_UPDATE_PREFERENCES}_SUCCESS`: {
      const updatesSuc = { ...state.manageUpdates };
      updatesSuc[action.blockType].preference[action.key] = action.flag;
      return {
        ...state,
        manageUpdates: updatesSuc
      };
    }

    case `${authTypes.ME_ACCESS_CONTROL}_SUCCESS`:
      return {
        ...state,
        accessControl: action.result,
        checkPartPaymentStatus: {
          ...state.checkPartPaymentStatus,
          [action.key]: action.result.check_part_payment_status
        }
      };

    case `${combatTypes.UPDATE_CONTEST_ONBOARDING_PREFERENCES}_SUCCESS`: {
      const { selectedClass, preferenceType } = action;
      return {
        ...state,
        accessControl: {
          ...state.accessControl,
          onboarding_pref_list: [
            {
              ...selectedClass,
              type: preferenceType,
              pref_type: preferenceType
            }
          ]
        }
      };
    }

    case topologyTypes.UPDATE_GLO_STATUS: {
      const { status } = action;
      return {
        ...state,
        accessControl: {
          ...state.accessControl,
          user_onboarding_status: status
        }
      };
    }

    case authTypes.PRE_PAYMENT_PLUS_SUBSCRIPTION_UIDS: {
      const { uids } = action;
      return {
        ...state,
        me: {
          ...state.me,
          prePaymentPlusSubscriptionUids: uids
        }
      };
    }

    case authTypes.HIDE_REFERRAL_BANNER: {
      return {
        ...state,
        showReferralBanner: action.show || false
      };
    }
    default: {
      const actionHandler = reducerMap[action.type];
      return actionHandler
        ? {
            ...state,
            ...actionHandler(action, state)
          }
        : state;
    }
  }
}
